// cecTableTools.js
// Mixin fábrica para tabelas com filtros + paginação na AppFooterTools
//
// Como usar:
//   import { createCecTableToolsMixin } from '@/mixins/cecTableTools'
//   mixins: [createCecTableToolsMixin({
//     rowsProp: 'dados',
//     tblRef: 'tbl',
//     filters: [
//       { key:'quantidade', type:'number', label:'Quantidade' },
//       { key:'situacao',   type:'string', label:'Situação', labelMap:{ '1':'Aberta','2':'Parcial','3':'Fechada' } }
//     ]
//   })]
//
// Requisitos no template:
//   - <b-table ref="tbl" :items="itensFiltrados" :per-page="perPage" :current-page="currentPage" :sticky-header="stickyHeight" ... >
//   - Seus <SetFilter> devem usar :options="opcoes.<key>" e :normalizeFn="normalize"
//   - AppFooterTools no layout (Full.vue) escutando 'tools:show'/'tools:update'/'tools:hide'
//
// O mixin expõe no componente:
//   data:     currentPage, perPage, stickyHeight, filtros (por chave)
//   computed: itensFiltrados, totalRows, opcoes (mapa), tagsAtivas, variantClass, normalize
//   methods:  onRemoveTag, onClear, onPageChange, onPerPageChange
//
// Observação: mantém aliases opcoesQtd/opcoesSituacao automaticamente se existirem essas chaves.

import { normalize as _norm } from '@/utils/filtrosTabela.js'

// (1) helper local: de preferência Vuex; fallback localStorage
function _getIsDark(vm, ns = 'tema', getter = 'IS_DARK') {
  try {
    if (vm?.$store?.getters && (`${ns}/${getter}` in vm.$store.getters)) {
      return !!vm.$store.getters[`${ns}/${getter}`]
    }
  } catch (_) { }
  return localStorage.getItem('tema') === 'D'
}

// helper único para exibir valores
function displayText(f, v) {
  // 1) prioridade para toText configurado no filtro
  if (typeof f.toText === 'function') return f.toText(v, f)

  // 2) labelMap (aceita chave number ou string)
  if (f.labelMap && (v in f.labelMap)) return f.labelMap[v]
  if (f.labelMap && (String(v) in f.labelMap)) return f.labelMap[String(v)]

  // 3) fallback por tipo
  const t = (f.type || 'string').toLowerCase()
  if (t === 'date' && Number.isFinite(v)) {
    return new Date(v).toLocaleDateString('pt-BR')
  }
  if (t === 'boolean' || t === 'bool') {
    return v ? 'Sim' : 'Não'
  }
  return String(v)
}

export function createCecTableToolsMixin(userCfg = {}, opts = {}) {
  const { useExternalPaging = true } = opts   // <- default: usa paginação externa (Vuex)

  const cfg = {
    themeNamespace: 'tema',
    themeGetter: 'IS_DARK',

    // nome da propriedade/array com as linhas (no componente hospedeiro)
    rowsProp: 'dados',

    transProps: {
      // Transition name
      name: 'flip-list'
    },
    // ref do <b-table> para calcular sticky
    tblRef: 'tbl',

    // seletor do scroller principal (CoreUI usa .main)
    scrollerSelector: '.main',

    // seletor das barras inferiores (para descontar altura)
    footerToolsSelector: '.app-footertools',
    footerSelector: '.app-footer',

    // eventos do "bus" no root
    eventNames: { show: 'tools:show', update: 'tools:update', hide: 'tools:hide' },

    // filtros (definição por coluna)
    //  key: nome da coluna em "rows"
    //  type: 'number' | 'string' (para normalizar/converter e ordenar)
    //  label: rótulo humano (usado só nas tags)
    //  labelMap (opcional): mapa value->texto para mostrar nas tags/opções
    filters: [],

    // valores padrão de paginação / sticky
    perPageDefault: 25,
    currentPageDefault: 1,
    minSticky: 240,
    stickyPadding: 8,

    // NOVO: como calcular a altura: 'auto' | 'scroller' | 'viewport'
    heightMode: 'auto',
    // NOVO: preferir ao menos X vh (ex.: 72 => 72vh). Deixe null/undefined para não forçar.
    preferVh: 75,
    // NOVO: atraso p/ recálculo depois de exibir a barra
    postShowRecalcDelay: 60,

    // normalizador (acentos/maiúsculas) opcional
    normalizeFn: s => _norm(String(s)),

    initSelect: 'none',          // inicia SEM nada marcado
    clearSelect: 'none',          //  <-- usar NONE ao limpar
    rowsChangeSelect: 'keep',    // opcional: manter seleção ao trocar dataset

    ...userCfg
  }

  // helpers locais

  const parseMoney = v => {
    if (v == null) return 0  // Retorna 0 em vez de NaN para null/undefined
    const s = String(v).trim()
      .replace(/[^\d,.-]/g, '')    // tira R$, espaços, etc.
      .replace(/\.(?=\d{3}(?:\D|$))/g, '') // remove milhares com ponto
      .replace(',', '.')           // vírgula decimal -> ponto
    const n = Number(s)
    return Number.isFinite(n) ? n : 0  // Retorna 0 em vez de NaN para valores inválidos
  }

  /*
   const parseMoney = v => {
     console.log(v)
     if (v == null) return NaN
     const s = String(v).trim()
       .replace(/[^\d,.-]/g, '')    // tira R$, espaços, etc.
       .replace(/\.(?=\d{3}(?:\D|$))/g, '') // remove milhares com ponto
       .replace('.', ',')           // vírgula decimal -> ponto
     const n = Number(s)
     return Number.isFinite(n) ? n : NaN
   }
 */
  const parseDate = v => {
    if (v instanceof Date) return v.getTime()
    if (typeof v === 'number') return v
    if (typeof v === 'string') {
      // tenta ISO ou Date.parse
      const t1 = Date.parse(v)
      if (!Number.isNaN(t1)) return t1
      // tenta DD/MM/YYYY
      const m = v.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
      if (m) {
        const [_, d, mo, y] = m
        const t2 = new Date(Number(y), Number(mo) - 1, Number(d)).getTime()
        if (!Number.isNaN(t2)) return t2
      }
    }
    return 0  // Retorna 0 em vez de NaN para valores inválidos
  }

  const castByType = (type, v) => {
    const t = (type || 'string').toLowerCase()
    if (t === 'number' || t === 'int' || t === 'float' || t === 'decimal') {
      const num = Number(v)
      return Number.isFinite(num) ? num : 0  // Retorna 0 para null/undefined/NaN
    }
    if (t === 'money' || t === 'currency') return parseMoney(v)
    if (t === 'date') return parseDate(v)
    if (t === 'boolean' || t === 'bool') return (String(v).toLowerCase() === 'true' || v === true || v === 1)
    return String(v) // default: string
  }

  const sortByType = (type, arr) => {
    const t = (type || 'string').toLowerCase()
    if (t === 'number' || t === 'int' || t === 'float' || t === 'decimal' || t === 'money' || t === 'currency' || t === 'date') {
      return [...arr].sort((a, b) => a - b)
    }
    if (t === 'boolean' || t === 'bool') {
      return [...arr].sort((a, b) => (a === b ? 0 : a ? 1 : -1))
    }
    return [...arr].sort() // string
  }

  // constrói computeds dinâmicos com base nos filtros
  const computedMap = {
    // ========= NOVO: computed reativo para tema =========
    __isDark() {
      return _getIsDark(this, cfg.themeNamespace, cfg.themeGetter)
    },

    // total de linhas após filtros
    totalRows() {
      return this.itensFiltrados.length
    },

    // normalizador passado pra SetFilter
    normalize() {
      return s => cfg.normalizeFn(s)
    },

    // mapa { key -> lista de valores únicos ordenados }
    _uniqueMap() {
      const rows = this[cfg.rowsProp] || []
      const map = {}
      for (const f of cfg.filters) {
        const vals = rows.map(r => castByType(f.type, r[f.key]))
        map[f.key] = sortByType(f.type, new Set(vals))
      }
      return map
    },

    opcoes() {
      const map = {}
      for (const f of cfg.filters) {
        const uniques = this._uniqueMap[f.key] || []
        map[f.key] = uniques.map(v => ({
          value: v,
          text: displayText(f, v)
        }))
      }
      return map
    },


    // linhas filtradas (pipeline pelas chaves definidas)
    itensFiltrados() {
      const rows = this[cfg.rowsProp] || []
      let out = rows

      for (const f of cfg.filters) {
        const uniques = this._uniqueMap[f.key] || []
        const selArr = (this.filtros?.[f.key]?.length ? this.filtros[f.key] : uniques)
        const cast = (v) => castByType(f.type, v)
        const selected = new Set(selArr.map(cast))
        out = out.filter(r => selected.has(cast(r[f.key])))
      }
      return out
    },

    // tags para barra inferior
    tagsAtivas() {
      const tags = []
      for (const f of cfg.filters) {
        const uniques = this._uniqueMap[f.key] || []
        const sel = this.filtros?.[f.key] || []
        const isAll = sel.length === 0 || sel.length === uniques.length
        if (!isAll) {
          for (const v of sel) {
            tags.push({ key: f.key, value: v, text: displayText(f, v) })
          }
        }
      }
      return tags
    },

    // visual da barra (respeita tema + se há filtros)
    variantClass() {
      const dark = this.__isDark
      return this.tagsAtivas.length
        ? (dark ? 'bg-dark text-light' : 'bg-light text-muted')
        : (dark ? 'bg-dark text-muted' : 'bg-light text-muted')
    },

    // ---- ALIASES (opcionais) para manter compatibilidade com páginas legadas ----
    ...(cfg.filters.some(f => f.key === 'quantidade') ? {
      opcoesQtd() { return this.opcoes.quantidade || [] }
    } : {}),
    ...(cfg.filters.some(f => f.key === 'situacao') ? {
      opcoesSituacao() { return this.opcoes.situacao || [] }
    } : {})
  }

  // watchers dinâmicos
  const watchMap = {
    // ao trocar items por página/página atual, ajusta sticky
    perPage() { this.$nextTick(this._updateSticky) },
    currentPage() { this.$nextTick(this._updateSticky) },

    // quando dataset mudar, restaura filtros e ressincroniza barra
    [cfg.rowsProp]: {
      deep: true,
      handler() {
        if (cfg.rowsChangeSelect === 'all' || cfg.rowsChangeSelect === 'none') {
          this._initAllFilters(cfg.rowsChangeSelect)
        }
        this.currentPage = 1
        this._syncTools()
        this.$nextTick(this._updateSticky)
      }
    },

    // quando filtros mudarem (via tags), volta para página 1 + re-sync
    tagsAtivas: {
      deep: true,
      handler() {
        this.currentPage = 1
        this._syncTools()
        this.$nextTick(this._updateSticky)
      }
    },

    // ========= NOVO: quando o tema mudar, re-sync a barra =========
    __isDark() {
      // atualiza a AppFooterTools (vai pegar o novo variantClass)
      this._syncTools()
      // se seu tema também altera alturas/cores que influem na sticky, recalcule:
      this.$nextTick(this._updateSticky)
    }

  }

  return {
    data() {
      // cria estrutura de filtros com arrays vazios por chave
      const filtros = {}
      for (const f of cfg.filters) filtros[f.key] = []

      return {
        // altura stick da b-table
        stickyHeight: '60vh',
        // paginação
        currentPage: cfg.currentPageDefault,
        perPage: cfg.perPageDefault,
        // filtros (serão “preenchidos” no mounted com “todos”)
        filtros
      }
    },

    computed: computedMap,
    watch: watchMap,

    methods: {
      // remove uma tag
      onRemoveTag(tag) { this._removeTag(tag) },
      // limpar todos filtros
      onClear() { this._initAllFilters(cfg.clearSelect) }, // “Remover filtro” → todos

      _removeTag(tag) {
        const arr = this.filtros[tag.key] || []
        this.filtros[tag.key] = arr.filter(v => String(v) !== String(tag.value))
      },

      _initAllFilters(mode = 'all') {
        for (const f of cfg.filters) {
          const uniques = this._uniqueMap[f.key] || []
          this.filtros[f.key] = (mode === 'all') ? [...uniques] : []
        }
      },

      // paginação
      onPageChange(p) { this.currentPage = p },
      onPerPageChange(n) {
        this.perPage = Number(n) || this.perPage
        this.currentPage = 1
        this._syncTools()
      },

      // envia estado para a barra no rodapé
      _syncTools() {
        const ev = cfg.eventNames
        const max = Math.max(1, Math.ceil(this.totalRows / this.perPage) || 1)
        if (this.currentPage > max) this.currentPage = max

        this.$root.$emit(ev.update, {
          tags: this.tagsAtivas,
          variantClass: this.variantClass,
          totalRows: this.totalRows,
          perPage: this.perPage,
          currentPage: this.currentPage
        })
      },

      // calcula :sticky-header respeitando scroller e barras inferiores
      _updateSticky() {
        const table = this.$refs[cfg.tblRef] && this.$refs[cfg.tblRef].$el
        if (!table) return

        const scroller = table.closest(cfg.scrollerSelector) || document.documentElement
        const scRect = scroller.getBoundingClientRect()
        const tbRect = table.getBoundingClientRect()

        const tools = document.querySelector(cfg.footerToolsSelector)
        const footer = document.querySelector(cfg.footerSelector)
        const hTools = tools ? tools.offsetHeight : 0
        const hFooter = footer ? footer.offsetHeight : 0

        const pad = cfg.stickyPadding
        // disponível medindo a partir do SCROLLER
        const availScroller = scroller.clientHeight - (tbRect.top - scRect.top) - hTools - hFooter - pad
        // disponível medindo a partir da VIEWPORT
        const availViewport = window.innerHeight - tbRect.top - hTools - hFooter - pad

        let avail;
        if (cfg.heightMode === 'scroller') avail = availScroller
        else if (cfg.heightMode === 'viewport') avail = availViewport
        else /* auto */                         avail = Math.max(availScroller, availViewport)

        // Respeita “preferência” em VH (ex.: 72vh)
        if (cfg.preferVh != null) {
          const preferPx = window.innerHeight * (Number(cfg.preferVh) / 100)
          avail = Math.max(avail, preferPx)
        }

        const min = cfg.minSticky
        const maxRef = (cfg.heightMode === 'scroller') ? scroller.clientHeight : window.innerHeight
        const max = maxRef - 40
        avail = Math.max(min, Math.min(max, avail))

        this.stickyHeight = `${Math.round(avail)}px`
      },

      _onResize() { this._updateSticky() }
    },

    mounted() {
      // inicia “todos selecionados” e mostra a barra
      this._initAllFilters(cfg.initSelect)

      const ev = cfg.eventNames
      this.$root.$emit(ev.show, {
        props: {
          tags: this.tagsAtivas,
          variantClass: this.variantClass,
          totalRows: this.totalRows,
          perPage: this.perPage,
          currentPage: this.currentPage
        },
        listeners: {
          'remove-tag': this.onRemoveTag,
          'clear': this.onClear,
          'page-change': this.onPageChange,
          'per-page-change': this.onPerPageChange
        }
      })

      this.$nextTick(() => {
        this._updateSticky()
        // recálculo extra depois da barra existir de fato
        setTimeout(() => this._updateSticky(), cfg.postShowRecalcDelay)
        window.addEventListener('resize', this._onResize, { passive: true })
      })
    },

    beforeDestroy() {
      const ev = cfg.eventNames
      this.$root.$emit(ev.hide)
      window.removeEventListener('resize', this._onResize)
    }
  }
}



// WEBPACK FOOTER //
// ./src/mixins/cecTableTools.js